import { lazy, Suspense, ReactNode } from 'react';
import { Route, Routes, Navigate, useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ProtectedRoute from '../components/ProtectedRoute';
import PermissionProtectedRoute from '../components/PermissionProtectedRoute';
import { VerificationGuard } from '../components/auth/VerificationGuard';
import PageTitle from '../components/PageTitle';
import { useReferralTracker } from '../hooks/useReferralTracker';

// Route Groups
import MarketingRoutes from './MarketingRoutes';
import DashboardRoutes from './DashboardRoutes';
import AdminRoutes from './AdminRoutes';

// Top-level Pages
const ReferralLandingPage = lazy(() => import('../pages/ReferralLandingPage'));

// Top-level Pages
const LoginPage = lazy(() => import('../pages/LoginPage'));
const SignupPage = lazy(() => import('../pages/SignupPage'));
const CustomerSignupPage = lazy(() => import('../pages/CustomerSignupPage'));
const TradeSignupPage = lazy(() => import('../pages/TradeSignupPage'));
const ResetPasswordPage = lazy(() => import('../pages/ResetPasswordPage'));
const UpdatePasswordPage = lazy(() => import('../pages/UpdatePasswordPage'));
const AuthCallbackPage = lazy(() => import('../pages/AuthCallbackPage'));
const OnSitePaymentLandingPage = lazy(() => import('../pages/OnSitePaymentLandingPage'));

// Protected Top-level Pages
const DocumentsPage = lazy(() => import('../pages/DocumentsPage'));
const HelpDeskLibraryPage = lazy(() => import('../pages/HelpDeskLibraryPage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const NotificationPreferencesPage = lazy(() => import('../pages/NotificationPreferencesPage'));

// Settings Pages
const ProfileSettingsPage = lazy(() => import('../pages/ProfileSettingsPage'));
const CompanySettingsPage = lazy(() => import('../pages/CompanySettingsPage'));
const CompanyMembersPage = lazy(() => import('../pages/CompanyMembersPage'));
const CompanyJobSettings = lazy(() => import('../pages/settings/CompanyJobSettings'));
const TradeRateCardPage = lazy(() => import('../pages/settings/TradeRateCardPage'));
const CompanyVariationSettings = lazy(() => import('../pages/settings/CompanyVariationSettings'));
const SettingsPayoutsPage = lazy(() => import('../pages/settings/SettingsPayoutsPage'));
const SettingsBillingPage = lazy(() => import('../pages/settings/SettingsBillingPage'));
const ApiConfigurationPage = lazy(() => import('../pages/settings/ApiConfigurationPage'));
const ClientWorkflowSettingsPage = lazy(() => import('../pages/settings/ClientWorkflowSettingsPage'));
const InductionsSettingsPage = lazy(() => import('../pages/settings/InductionsSettingsPage'));
const WorkflowTemplateEditorPage = lazy(() => import('../pages/workflow-templates/WorkflowTemplateEditorPage'));
const VariationsManagementPage = lazy(() => import('../pages/VariationsManagementPage'));
const TradeVariationsPage = lazy(() => import('../pages/TradeVariationsPage'));
const ClientLedgerPage = lazy(() => import('../pages/billing/ClientLedgerPage'));
const InvoicesPage = lazy(() => import('../pages/billing/InvoicesPage'));
const IntegrationsSettingsPage = lazy(() => import('../pages/settings/IntegrationsSettingsPage'));
const CrewsPage = lazy(() => import('../pages/CrewsPage'));

// Public Scheduling Pages (magic-link access)
const PublicSchedulingPage = lazy(() => import('../pages/PublicSchedulingPage'));
const PublicReschedulePage = lazy(() => import('../pages/PublicReschedulePage'));
const StaffSchedulePage = lazy(() => import('../pages/StaffSchedulePage'));

// Public no-login compliance document upload (magic-link access)
const ComplianceUploadPage = lazy(() => import('../pages/ComplianceUploadPage'));

// Public anonymous job teaser (shareable growth link, no auth required)
const PublicJobTeaserPage = lazy(() => import('../pages/PublicJobTeaserPage'));

// Material management — supplier/warehouse verification surfaces.
const SupplierVerifyPage = lazy(() => import('../pages/SupplierVerifyPage'));
const WarehouseVerifyPage = lazy(() => import('../pages/WarehouseVerifyPage'));
const AdminLocationsPage = lazy(() => import('../pages/AdminLocationsPage'));
const AdminProductsPage = lazy(() => import('../pages/AdminProductsPage'));
const AdminStagingConsolePage = lazy(() => import('../pages/AdminStagingConsolePage'));
const AdminVehiclesPage = lazy(() => import('../pages/AdminVehiclesPage'));
const TradeVehiclesPage = lazy(() => import('../pages/TradeVehiclesPage'));
const LogisticsDispatcherPage = lazy(() => import('../pages/LogisticsDispatcherPage'));


function RouteFallback() {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
        </Box>
    );
}

function suspense(element: ReactNode) {
    return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function LegacyJobRedirect() {
    const { jobId } = useParams<{ jobId: string }>();
    const location = useLocation();

    if (!jobId || !UUID_REGEX.test(jobId)) {
        return <Navigate to="/" replace />;
    }

    return (
        <Navigate
            to={`/dashboard/jobs/${jobId}${location.search}${location.hash}`}
            replace
        />
    );
}

export function BillingRedirect() {
    const location = useLocation();
    return (
        <Navigate
            to={{ pathname: '/dashboard/financials', search: location.search }}
            replace
        />
    );
}

export default function AppRoutes() {
    useReferralTracker();
    return (
        <Routes>
            {/* Marketing & Public Routes */}
            <Route path="/*" element={<MarketingRoutes />} />

            {/* Admin Module */}
            <Route path="/admin/*" element={<AdminRoutes />} />

            {/* Dashboard Module */}
            <Route path="/dashboard/*" element={<DashboardRoutes />} />

            {/* Auth */}
            <Route path="/auth/callback" element={<PageTitle title="Authenticating">{suspense(<AuthCallbackPage />)}</PageTitle>} />
            <Route path="/login" element={<PageTitle title="Login">{suspense(<LoginPage />)}</PageTitle>} />
            <Route path="/signup" element={<PageTitle title="Sign Up">{suspense(<SignupPage />)}</PageTitle>} />
            <Route path="/r/:code" element={<PageTitle title="Welcome to Trade Connector">{suspense(<ReferralLandingPage />)}</PageTitle>} />
            <Route path="/signup/client" element={<PageTitle title="Client Sign Up">{suspense(<CustomerSignupPage />)}</PageTitle>} />
            <Route path="/signup/trade" element={<PageTitle title="Trade Sign Up">{suspense(<TradeSignupPage />)}</PageTitle>} />
            {/* Retired legacy sign-up flows (their edge functions now answer 410): send anyone with an
                old link to the live sign-up chooser. The page files stay; other pages import their types. */}
            <Route path="/signup/dual-role" element={<Navigate to="/signup" replace />} />
            <Route path="/signup-enhanced" element={<Navigate to="/signup" replace />} />
            <Route path="/partner-signup" element={<Navigate to="/signup" replace />} />
            <Route path="/reset-password" element={<PageTitle title="Reset Password">{suspense(<ResetPasswordPage />)}</PageTitle>} />
            <Route path="/update-password" element={<PageTitle title="Set Password">{suspense(<UpdatePasswordPage />)}</PageTitle>} />

            {/* Public Scheduling Routes (magic-link, no auth required) */}
            <Route path="/schedule/:token" element={<PageTitle title="Job Scheduling">{suspense(<PublicSchedulingPage />)}</PageTitle>} />
            <Route path="/schedule" element={<PageTitle title="Job Scheduling">{suspense(<PublicSchedulingPage />)}</PageTitle>} />
            <Route path="/reschedule/:token" element={<PageTitle title="Reschedule Job">{suspense(<PublicReschedulePage />)}</PageTitle>} />
            <Route path="/reschedule" element={<PageTitle title="Reschedule Job">{suspense(<PublicReschedulePage />)}</PageTitle>} />
            <Route path="/upload/:token" element={<PageTitle title="Upload Your Documents">{suspense(<ComplianceUploadPage />)}</PageTitle>} />
            <Route path="/upload" element={<PageTitle title="Upload Your Documents">{suspense(<ComplianceUploadPage />)}</PageTitle>} />
            <Route path="/jobs/teasers/:token" element={<PageTitle title="Job Opportunity">{suspense(<PublicJobTeaserPage />)}</PageTitle>} />
            <Route path="/jobs/teasers" element={<PageTitle title="Job Opportunity">{suspense(<PublicJobTeaserPage />)}</PageTitle>} />
            <Route path="/jobs/:jobId" element={<LegacyJobRedirect />} />
            <Route path="/supplier/verify" element={<PageTitle title="Supplier Verification">{suspense(<SupplierVerifyPage />)}</PageTitle>} />
            <Route path="/warehouse/verify" element={<PageTitle title="Warehouse Verification">{suspense(<WarehouseVerifyPage />)}</PageTitle>} />

            {/* On-site payment landings — Stripe Checkout success/cancel returns */}
            <Route path="/pay/success" element={suspense(<OnSitePaymentLandingPage variant="success" />)} />
            <Route path="/pay/cancelled" element={suspense(<OnSitePaymentLandingPage variant="cancelled" />)} />

            {/* Protected Routes (Top Level but require Auth) */}

            <Route
                path="/library"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Help Center Library">{suspense(<HelpDeskLibraryPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />

            <Route
                path="/documents"
                element={
                    <PermissionProtectedRoute requiredPermissions={['document:view', 'document:upload']} requireAllPermissions={false}>
                        <PageTitle title="Documents">{suspense(<DocumentsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />

            <Route
                path="/notifications"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Notifications">{suspense(<NotificationsPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />

            {/* Settings Group */}
            <Route
                path="/settings/profile"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Profile Settings">{suspense(<ProfileSettingsPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/settings/company"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Company Settings">{suspense(<CompanySettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/team"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:manage_users', 'user:invite']} requireAllPermissions={false}>
                        <PageTitle title="Team Members">{suspense(<CompanyMembersPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/crews"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:manage_users', 'company:edit']} requireAllPermissions={false}>
                        <PageTitle title="Crews">{suspense(<CrewsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/company-jobs"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Job Settings">{suspense(<CompanyJobSettings />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/rates"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Rate Card">{suspense(<TradeRateCardPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/webhooks"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <Navigate to="/settings/api?tab=webhooks" replace />
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/api"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="API & Webhooks">{suspense(<ApiConfigurationPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/integrations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Integrations">{suspense(<IntegrationsSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/workflows"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Workflow Settings">{suspense(<ClientWorkflowSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/workflows/new"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="New Workflow">{suspense(<WorkflowTemplateEditorPage mode="customer" />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/workflows/edit/:id"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Edit Workflow">{suspense(<WorkflowTemplateEditorPage mode="customer" />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/workflows/preview/:id"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:view']}>
                        <PageTitle title="Preview Workflow">{suspense(<WorkflowTemplateEditorPage mode="customer" readOnly />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/inductions"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Inductions">{suspense(<InductionsSettingsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/notifications"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Notification Preferences">{suspense(<NotificationPreferencesPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />


            {/* Variation Management */}
            <Route
                path="/variations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Variations">{suspense(<VariationsManagementPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/trade-variations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <VerificationGuard>
                            <PageTitle title="Trade Variations">{suspense(<TradeVariationsPage />)}</PageTitle>
                        </VerificationGuard>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/variations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Variation Settings">{suspense(<CompanyVariationSettings />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/payouts"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Payout Settings">{suspense(<SettingsPayoutsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/settings/billing"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Billing Settings">{suspense(<SettingsBillingPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/billing/ledger"
                element={
                    <PermissionProtectedRoute requiredPermissions={['company:edit']}>
                        <PageTitle title="Ledger">{suspense(<ClientLedgerPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/billing/invoices"
                element={
                    <PermissionProtectedRoute requiredPermissions={['job:view']}>
                        <PageTitle title="Invoices">{suspense(<InvoicesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route path="/billing" element={<BillingRedirect />} />
            <Route path="/billing/terms-charges" element={<BillingRedirect />} />

            {/* Material Logistics — authenticated routes */}
            <Route
                path="/admin/locations"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Warehouses & Locations">{suspense(<AdminLocationsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/admin/products"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Products & Stock">{suspense(<AdminProductsPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/admin/staging"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Warehouse Staging">{suspense(<AdminStagingConsolePage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/admin/vehicles"
                element={
                    <PermissionProtectedRoute requiredPermissions={['platform:view_all']}>
                        <PageTitle title="Fleet / Vehicles">{suspense(<AdminVehiclesPage />)}</PageTitle>
                    </PermissionProtectedRoute>
                }
            />
            <Route
                path="/dashboard/vehicles"
                element={
                    <ProtectedRoute>
                        <PageTitle title="My Vehicles">{suspense(<TradeVehiclesPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/dashboard/dispatch/:jobId"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Dispatch Logistics Ticket">{suspense(<LogisticsDispatcherPage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />

            {/* Staff Schedule */}
            <Route
                path="/staff-schedule"
                element={
                    <ProtectedRoute>
                        <PageTitle title="Staff Schedule">{suspense(<StaffSchedulePage />)}</PageTitle>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
}
